import { predictionsApis } from '@gleamer/apis';
import { Internal, Mapper, OHIF } from '@gleamer/types';
import { DisplaySetService, ServicesManager } from '@ohif/core';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  useSetCurrentPredictionsKey,
  useSetPredictions,
} from './predictions.hooks';

type UserAuthenticationService = OHIF.UserAuthenticationService;

/** The preview a URL asks for: `predictionImport`, `predictionProduct`, and the study it names; undefined when none. */
export function previewFromSearch(search: string): predictionsApis.PredictionPreview | undefined {
  const params = new URLSearchParams(search);
  const importId = Number(params.get('predictionImport')) || undefined;
  const productId = Number(params.get('predictionProduct')) || undefined;
  if (importId === undefined && productId === undefined) {
    return undefined;
  }
  return { importId, productId, studyInstanceUID: params.get('studyInstanceUID') ?? undefined };
}

/** The series a case shows, in its studies' order: what a task's predictions are read for. */
export function seriesOfItem(item: Internal.LabelTaskItem | null | undefined): string[] {
  return (item?.studies ?? []).flatMap(study =>
    (study.series ?? []).map(series => series.SeriesInstanceUID)
  );
}

/**
 * The predictions on the case's series only: one on a series the viewer did not load is never asked about, so it
 * is never saved with the pass. A server that ignores the series filter is held to it here too.
 */
export function onTheCase<T extends { seriesInstanceUID: string }>(
  predictions: T[],
  seriesInstanceUIDs: string[]
): T[] {
  if (seriesInstanceUIDs.length === 0) {
    return predictions;
  }
  const shown = new Set(seriesInstanceUIDs);
  return predictions.filter(prediction => shown.has(prediction.seriesInstanceUID));
}

export function usePredictionsQuery({
  servicesManager,
  patientId,
  taskId,
  seriesInstanceUIDs = [],
}: {
  servicesManager: ServicesManager;
  patientId: string;
  taskId?: number;
  /** A task's case: the series it shows. */
  seriesInstanceUIDs?: string[];
}) {
  const { userAuthenticationService, displaySetService } =
    servicesManager.services as {
      userAuthenticationService: UserAuthenticationService;
      displaySetService: DisplaySetService;
    };

  const setCurrentPredictionsKey = useSetCurrentPredictionsKey();

  if (taskId && patientId) {
    setCurrentPredictionsKey(`predictions-${patientId}-${taskId}`);
  }

  const setPredictions = useSetPredictions();
  // the preview mode opened from the Prediction imports page: an import's predictions, or a product's current ones
  const preview = taskId
    ? undefined
    : previewFromSearch(window.location.search);
  const series = taskId ? [...new Set(seriesInstanceUIDs)].sort() : [];

  const queryResult = useQuery({
    queryKey: ['predictions', patientId, taskId, preview, series],
    queryFn: async () => {
      const data = await predictionsApis.fetchPredictions(
        userAuthenticationService,
        patientId,
        taskId,
        preview,
        series
      );
      return {
        ...data,
        predictions: await Promise.all(
          onTheCase(data.predictions, series).map((prediction) => {
            let displaySet = displaySetService
              .getDisplaySetsForSeries(prediction.seriesInstanceUID)
              .find(
                (ds) => ds.StudyInstanceUID === prediction.studyInstanceUID
              );
            prediction.algorithm = data.algorithm;
            return Mapper.PredictionMapper.toPredictionInternalWithDisplaySet(
              prediction,
              displaySet?.SeriesInstanceUID
            );
          })
        ),
      };
    },
    enabled: !!patientId,
  });

  const { data, error } = queryResult;

  useEffect(() => {
    if (data) {
      setPredictions({
        predictions: data.predictions,
        overrideInitialPredictions: true,
      });
    }
    if (error) {
      setPredictions({ predictions: [], overrideInitialPredictions: true });
    }
  }, [data, error]);

  return queryResult;
}
